import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { archiveOrbitNodes } from './archiveData';
const fade = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: index * 0.08,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
function ArchiveOrbit() {
  const [active, setActive] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const size = 560;
  const center = size / 2;
  const radius = 220;
  const positions = archiveOrbitNodes.map((_, index) => {
    const angle = index / archiveOrbitNodes.length * Math.PI * 2 - Math.PI / 2;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  });
  return <div className="relative mx-auto" style={{
    width: size,
    height: size,
    maxWidth: '100%'
  }} data-id="element-131" data-mp="4iinkmdw3qzo">
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-132" data-mp="2qgavooa6p74f">
        {[0.34, 0.62, 1].map(ring => <circle key={ring} cx={center} cy={center} r={radius * ring} fill="none" stroke="#33a7b5" strokeOpacity={0.14} strokeWidth={1} data-id="element-133" data-mp="2zgftq7thmt5x" />)}
        {positions.map((position, index) => {
        const isActive = index === active;
        return <motion.line key={archiveOrbitNodes[index].id} x1={center} y1={center} x2={position.x} y2={position.y} stroke="#33a7b5" initial={false} animate={{
          strokeOpacity: isActive ? 0.9 : 0.16,
          strokeWidth: isActive ? 2.5 : 1
        }} transition={{
          duration: shouldReduceMotion ? 0 : 0.25
        }} data-id="element-134" data-mp="3ebvs2p13pb84" data-mpi={index} />;
      })}
      </svg>

      <div className="absolute left-1/2 top-1/2 flex h-[176px] w-[176px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-navy px-7 py-5 text-center shadow-2xl shadow-navy/30" data-id="element-135" data-mp="1wzvsgyk3w6ry">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-teal-light" data-id="element-136" data-mp="2211mczpi6032">
          JAG Medical Solutions
        </p>
        <motion.p key={active} initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.2
      }} className="mt-1.5 max-w-[144px] break-words font-display text-[15px] font-extrabold leading-[1.1] text-white" data-id="element-137" data-mp="2frokapnfvly3">
          {archiveOrbitNodes[active].name}
        </motion.p>
        <motion.p key={`detail-${active}`} initial={shouldReduceMotion ? false : {
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.25
      }} className="mt-1.5 max-w-[142px] text-[11px] leading-[1.25] text-white/70" data-id="element-138" data-mp="vdnxr6rccesp">
          {archiveOrbitNodes[active].detail}
        </motion.p>
      </div>

      {positions.map((position, index) => {
      const node = archiveOrbitNodes[index];
      const isActive = index === active;
      return <div key={node.id} className="absolute" style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-139" data-mp="2x82ae370unwk" data-mpi={index}>
            {isActive && <motion.span className="pointer-events-none absolute inset-0 rounded-full border-2 border-teal/60" initial={false} animate={shouldReduceMotion ? {
          opacity: 0.3,
          scale: 1.08
        } : {
          opacity: [0.38, 0, 0.38],
          scale: [1, 1.18, 1]
        }} transition={shouldReduceMotion ? {
          duration: 0
        } : {
          duration: 2.8,
          ease: 'easeInOut',
          repeat: Infinity
        }} data-id="element-140" data-mp="29dhlp2y03ita" />}
            <motion.button type="button" onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)} onClick={() => setActive(index)} initial={false} animate={{
          scale: isActive ? 1.035 : 1,
          y: 0
        }} whileHover={shouldReduceMotion ? undefined : {
          scale: 1.045,
          y: -3
        }} whileTap={shouldReduceMotion ? undefined : {
          scale: 0.98
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 24
        }} className={`relative flex h-[90px] w-[90px] cursor-pointer flex-col items-center justify-center rounded-full border-2 px-2 text-center outline-none transition-[background-color,border-color,box-shadow] duration-200 focus-visible:ring-[3px] focus-visible:ring-navy focus-visible:ring-offset-4 focus-visible:ring-offset-white ${isActive ? 'z-10 border-teal-light bg-teal text-white shadow-[0_16px_30px_-12px_rgba(51,167,181,0.85)]' : 'border-transparent bg-white text-navy shadow-[0_10px_22px_-14px_rgba(26,27,75,0.4)] hover:border-teal/70 hover:bg-slate-50 hover:shadow-[0_16px_28px_-12px_rgba(51,167,181,0.45)]'}`} aria-label={node.name} aria-pressed={isActive} data-id="element-141" data-mp="1zl2wzt5irf42">
              <span className={`max-w-[76px] leading-[1.08] ${isActive ? 'text-[12px] font-extrabold' : 'text-[11px] font-bold'}`} data-id="element-142" data-mp="e0xvcbqiv511">
                {node.id === 'peptides-stem-cells' ? <>
                    <span className="block whitespace-nowrap" data-id="element-143" data-mp="3mjvn27wqkci9">Peptides /</span>
                    <span className="block whitespace-nowrap" data-id="element-144" data-mp="3rhx6wv1zkfus">Stem Cells</span>
                  </> : node.name}
              </span>
            </motion.button>
          </div>;
    })}
    </div>;
}
export function ArchiveHero() {
  return <section className="relative w-full max-w-[100vw] overflow-hidden bg-white" data-id="element-145" data-mp="3t7tww06m7ma7">
      <img src="https://cdn.magicpatterns.com/uploads/jcxmq9exrVrKEqcEaZF8dP/jag-hero-balanced-room-v3.png" alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[72%_center] opacity-[0.34] sm:opacity-[0.44] lg:opacity-[0.78]" data-id="element-146" data-mp="3o87j9pmvk40v" />
      <div className="pointer-events-none absolute inset-0 bg-white/68 sm:bg-white/60 lg:bg-white/24" data-id="element-147" data-mp="3ahc826owlau4" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-full bg-white/58 sm:bg-white/52 lg:w-[54%] lg:bg-white/82" data-id="element-148" data-mp="35hgel1du11st" />
      <div className="relative mx-auto grid w-full max-w-[100vw] items-center gap-10 px-5 py-14 sm:max-w-7xl sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:py-20" data-id="element-149" data-mp="pp0lnu1b79j7">
        <div className="w-full min-w-0 max-w-xs sm:max-w-xl" data-id="element-150" data-mp="sagrz33t6uc7">
          <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="w-full min-w-0 max-w-xs whitespace-normal break-words text-[11px] font-semibold uppercase tracking-[0.08em] text-teal sm:max-w-xl sm:text-[12px] sm:tracking-[0.16em] lg:text-[13px] xl:text-[14px]" data-id="element-151" data-mp="sd30b4yixdvh">
            Equipment Guidance for Rehabilitation and Wellness Practices
          </motion.p>
          <motion.h1 variants={fade} initial="hidden" animate="show" custom={1} className="mt-4 w-full min-w-0 max-w-xs whitespace-normal break-words font-display text-[1.6rem] font-extrabold leading-[1.08] tracking-tight text-navy sm:max-w-xl sm:text-5xl sm:leading-[1.05] lg:text-[3.75rem] xl:text-[4rem]" data-id="element-152" data-mp="1g0udearw1uxt">
            Curated Modality Solutions for Your Practice
          </motion.h1>
          <motion.p variants={fade} initial="hidden" animate="show" custom={2} className="mt-6 w-full min-w-0 max-w-xs whitespace-normal break-words text-[17px] leading-relaxed text-charcoal/75 sm:max-w-xl lg:text-[18px] xl:text-[19px] xl:leading-[1.65]" data-id="element-153" data-mp="1ysws3gkyz15f">
            JAG Medical Solutions helps chiropractors, physical therapists, and
            wellness practices compare proven technologies. As a trusted
            partner, our mission is to help you choose the equipment that suits
            your clinic&apos;s workflow and provides the best patient outcomes
          </motion.p>
          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-8 w-full max-w-xs sm:w-auto sm:max-w-none" data-id="element-154" data-mp="1izjchtjvyjpl">
            <a href="#products" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-7 py-3.5 text-[15px] font-semibold text-navy transition-colors hover:border-teal hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 sm:w-auto" data-id="element-155" data-mp="7n8rwafaptw8">
              Explore Product Categories
              <ArrowRightIcon className="h-[18px] w-[18px]" data-id="element-156" data-mp="2syu6nq53e7lc" />
            </a>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="hidden justify-center lg:flex" data-id="element-157" data-mp="2xyqczw19ib42">
          <ArchiveOrbit data-id="element-158" data-mp="3bvly4musft0h" />
        </motion.div>

        <div className="grid w-full min-w-0 max-w-xs grid-cols-1 gap-3 sm:max-w-none sm:grid-cols-2 lg:hidden" data-id="element-159" data-mp="1clvwlprx98zv">
          {archiveOrbitNodes.map(node => <div key={node.id} className="min-w-0 rounded-2xl border border-slate-100 bg-white/95 p-4 shadow-sm" data-id="element-160" data-mp="2wkdyyf1yhdgy">
              <p className="break-words font-display text-sm font-bold leading-tight text-navy" data-id="element-161" data-mp="31k0l5dzri41w">
                {node.name}
              </p>
              <p className="mt-1.5 break-words text-xs leading-snug text-charcoal/55" data-id="element-162" data-mp="3ak6wat7zmrxw">
                {node.detail}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}